// Okubo west (Koreatown streets and Golden Gai pockets): BBQ and chicken restaurants, cosmetics shops, Korean marts and
// tiny two-storey bars under paper lanterns. Kind names are prefixed "okuboWest". The shared glyph table has no hangul and
// few kanji, so this file draws the characters its signs need (GX) and falls back to the shared glyphs for the rest.
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, type G, px, dither, bands, disc, frame, shadow, dk, lt, alpha, text, textW, glyphW } from './hd-kit';
import { registerFront, registerRoofProp, type LightFn } from './hd-registry';
import { windowPane, display, door, awning, lantern, plant } from './hd-parts';

const tW = textW, tX = text;
const fitS = (t: string, w: number) => (tW(t, 3) + 12 <= w - 24 ? 3 : 2);
/** a neon band sign centred on cx */
function owBand(g: G, cx: number, y: number, txt: string, color: NeonColor, L: LightFn, s = 2) {
  const col = NEON[color], w = tW(txt, s) + 12, h = 6 * s + 10, x = cx - Math.floor(w / 2);
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.82));
  frame(g, x + 2, y + 2, w - 4, h - 4, col); frame(g, x + 3, y + 3, w - 6, h - 6, dk(col, 0.4));
  tX(g, txt, x + 6, y + 5, lt(col, 0.6), s);
  L(x, y, w, h, col, 0.6, true, true);
}
/** a vertical blade sign hung from the wall at x */
function owBlade(g: G, x: number, y: number, txt: string, color: NeonColor, L: LightFn, s = 2) {
  const col = NEON[color], chars = [...txt], cw = Math.max(...chars.map(glyphW)) * s, w = cw + 10, h = chars.length * (6 * s + 3) + 8;
  px(g, x - 3, y + 6, 3, 2, K.metalLo); px(g, x - 3, y + h - 8, 3, 2, K.metalLo);
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.8)); frame(g, x + 2, y + 2, w - 4, h - 4, col);
  chars.forEach((c, i) => tX(g, c, x + 5 + Math.floor((cw - glyphW(c) * s) / 2), y + 5 + i * (6 * s + 3), lt(col, 0.6), s));
  L(x, y, w, h, col, 0.6, true, true);
}

// ---------------------------------------------------------------- roof props
registerRoofProp('okuboWestFlue', ({ g, x, y, L }) => {
  // a kitchen exhaust hood with a fan cap and a thin plume of smoke
  shadow(g, x + 4, y + 5, 28, 22);
  px(g, x, y, 28, 22, K.ink); px(g, x + 1, y + 1, 26, 8, K.metalHi); px(g, x + 1, y + 9, 26, 12, K.metal); px(g, x + 1, y + 20, 26, 1, K.metalLo);
  disc(g, x + 14, y + 10, 8, 6, K.metalLo); disc(g, x + 14, y + 10, 5, 4, K.ink);
  for (let k = -4; k <= 4; k += 4) px(g, x + 14 + k, y + 9, 1, 3, K.metal);
  dither(g, x + 6, y - 10, 18, 10, alpha(K.white, 0.18)); dither(g, x + 10, y - 18, 12, 8, alpha(K.white, 0.1), 1);
  L(x + 4, y + 2, 20, 16, K.amber, 0.14);
});
const BOARDS = ['고기', '마트', '치킨', 'コスメ', '酒', '미용'];
registerRoofProp('okuboWestBoard', ({ g, x, y, L }) => {
  // a rooftop signboard on two legs, its text chosen by position
  const v = Math.floor(hash2(x, y) * BOARDS.length), t = BOARDS[v], col: NeonColor = (['pink', 'cyan', 'amber'] as const)[v % 3];
  const s = 2, w = tW(t, s) + 12;
  shadow(g, x + 4, y + 18, w, 10, 0.4);
  px(g, x + 4, y + 18, 3, 8, K.metalLo); px(g, x + w - 7, y + 18, 3, 8, K.metalLo);
  owBand(g, x + Math.floor(w / 2), y, t, col, L, s);
});

registerRoofProp('okuboWestLaundry', ({ g, x, y }) => {
  // a drying pole between two posts with shirts and a futon in palette colours
  shadow(g, x + 3, y + 22, 78, 6, 0.3);
  for (const px0 of [x, x + 76]) { px(g, px0, y + 4, 3, 20, K.ink); px(g, px0 + 1, y + 4, 1, 20, K.metalHi); }
  px(g, x, y + 5, 79, 2, K.metalLo);
  const cols = [K.white, K.pink, K.cyan, K.plasterLo, K.amber, K.white];
  cols.forEach((c, i) => { const cx = x + 6 + i * 12, ch = 10 + Math.floor(hash2(x + i, y) * 6); px(g, cx, y + 7, 9, ch, K.ink); px(g, cx + 1, y + 7, 7, ch - 1, dk(c, 0.35)); px(g, cx + 1, y + 7, 3, ch - 1, dk(c, 0.15)); });
});

// ---------------------------------------------------------------- fronts
/** window on the street: a lit room with tabletop grills, smoke and a glint */
function grillWindow(g: G, x: number, y: number, w: number, h: number, L: LightFn) {
  if (w < 16 || h < 12) return;
  px(g, x - 2, y - 2, w + 4, h + 4, K.ink); px(g, x - 1, y - 1, w + 2, h + 2, K.metalLo);
  bands(g, x, y, w, h, K.warmHi, K.warm, 4);
  for (let i = 10; i < w - 10; i += 44) {
    px(g, x + i - 12, y + h - 12, 26, 3, K.woodLo); px(g, x + i - 10, y + h - 9, 2, 9, K.woodLo); px(g, x + i + 12, y + h - 9, 2, 9, K.woodLo);
    disc(g, x + i + 1, y + h - 13, 8, 3, K.ink); disc(g, x + i + 1, y + h - 13, 5, 2, K.redLo); px(g, x + i - 1, y + h - 14, 3, 1, K.warmHi);
    dither(g, x + i - 6, y + h - 28, 14, 14, alpha(K.white, 0.22), i);
    disc(g, x + i - 14, y + h - 22, 4, 4, K.ink); px(g, x + i - 17, y + h - 18, 7, 6, K.ink); // diner silhouettes
    disc(g, x + i + 16, y + h - 22, 4, 4, K.ink); px(g, x + i + 13, y + h - 18, 7, 6, K.ink);
  }
  for (let k = 0; k < h; k++) px(g, x + Math.floor(w * 0.62) + Math.floor(k / 2), y + k, 2, 1, alpha(K.white, 0.16));
  for (let i = Math.floor(w / 3); i < w; i += Math.floor(w / 3)) px(g, x + i, y, 1, h, K.metalLo);
  L(x, y, w, h, K.amber, 0.38, true);
}
/** row of three noren curtains over a door, split like a shop curtain */
function noren(g: G, x: number, y: number, w: number) {
  const n = 3, nw = Math.floor(w / n);
  for (let i = 0; i < n; i++) { px(g, x + i * nw, y, nw - 1, 16, K.redLo); px(g, x + i * nw, y, nw - 1, 2, K.redDk); px(g, x + i * nw + 1, y + 2, 2, 12, lt(K.redLo, 0.25)); }
  disc(g, x + w / 2, y + 7, 4, 4, K.white);
}

registerFront('okuboWestBbq', { wall: dk(K.red, 0.62) }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  const low = h <= 64, sy = low ? y + 6 : gy - 22, top = gy + (low ? 12 : 8), sh = gy + gf - 4 - top;
  if (sign) owBand(g, x + Math.floor(w / 2), sy, sign.text, sign.color, L, fitS(sign.text, w));
  // exhaust duct down the side wall
  px(g, x + w - 18, y + 2, 9, h - 6, K.ink); px(g, x + w - 17, y + 2, 3, h - 6, K.metalHi); px(g, x + w - 14, y + 2, 4, h - 6, K.metal);
  for (let j = y + 10; j < y + h - 12; j += 18) px(g, x + w - 19, j, 11, 2, K.metalLo);
  const dw = 44, dx = x + Math.floor(w / 2) - dw / 2;
  grillWindow(g, x + 12, top, dx - 20 - (x + 12), sh - 2, L);
  grillWindow(g, dx + dw + 20, top, x + w - 26 - (dx + dw + 20), sh - 2, L);
  door(g, dx, top, dw, sh, K.red, L); noren(g, dx, top, dw);
  lantern(g, dx - 14, top + 2, K.red, L); lantern(g, dx + dw + 6, top + 2, K.red, L);
  if (!low) { owBlade(g, x + 6, y + 10, '고기', 'pink', L, 2); }
  plant(g, dx - 40 > x + 14 ? dx - 40 : x + 16, gy + gf - 20);
});

registerFront('okuboWestCosmetics', { wall: K.plaster }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  const low = h <= 64, sy = low ? y + 6 : gy - 22, top = gy + (low ? 12 : 8), sh = gy + gf - 4 - top;
  if (sign) owBand(g, x + Math.floor(w / 2), sy, sign.text, sign.color, L, fitS(sign.text, w));
  const dw = 40, dx = x + Math.floor(w / 2) - dw / 2;
  display(g, x + 14, top, dx - 24 - (x + 14), sh - 2, K.pink, L);
  display(g, dx + dw + 24, top, x + w - 14 - (dx + dw + 24), sh - 2, K.white, L);
  door(g, dx, top, dw, sh, K.white, L); frame(g, dx - 3, top - 3, dw + 6, sh + 3, K.pink);
  // star posters either side of the door, and neon strips up the corners
  for (const px0 of [dx - 18, dx + dw + 6]) { px(g, px0, top + 2, 12, 20, K.ink); px(g, px0 + 1, top + 3, 10, 18, dk(K.pink, 0.4)); disc(g, px0 + 6, top + 10, 3, 3, lt(K.pink, 0.5)); px(g, px0 + 3, top + 14, 6, 5, K.cyan); L(px0, top + 2, 12, 20, K.pink, 0.3, true); }
  for (const sx of [x + 7, x + w - 9]) { px(g, sx, gy + 4, 2, gf - 8, K.pink); L(sx - 2, gy + 4, 6, gf - 8, K.pink, 0.4, true); }
});

registerFront('okuboWestMart', { wall: K.plasterLo }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  const low = h <= 64, sy = low ? y + 6 : gy - 22, top = gy + (low ? 14 : 12);
  if (sign) owBand(g, x + Math.floor(w / 2), sy, sign.text, sign.color, L, fitS(sign.text, w));
  awning(g, x + 8, top - 6, w - 16, 7, K.cyan, K.white);
  const sh = gy + gf - 12 - (top + 8), dw = 52, dx = x + Math.floor(w / 2) - dw / 2;
  display(g, x + 14, top + 8, dx - 14 - (x + 14), sh, K.leaf, L);
  display(g, dx + dw + 14, top + 8, x + w - 14 - (dx + dw + 14), sh, K.leaf, L);
  door(g, dx, top + 6, dw, gf - 12 - (top - gy) + 4, K.white, L);
  // produce crates along the kerb
  for (let i = x + 16; i + 22 <= x + w - 16; i += 26) {
    if (i + 22 > dx - 4 && i < dx + dw + 4) continue;
    px(g, i, gy + gf - 14, 22, 11, K.ink); px(g, i + 1, gy + gf - 13, 20, 9, K.woodLo); px(g, i + 1, gy + gf - 13, 20, 1, K.woodHi);
    for (let k = 0; k < 6; k++) { const v = hash2(i + k, gy), c = v < 0.35 ? K.red : v < 0.7 ? K.leaf : K.amber; disc(g, i + 4 + k * 3, gy + gf - 15, 2, 2, c); }
  }
});

const GAI_TEXT = ['酒', '夜', 'バー', 'スナック'];
registerFront('okuboWestGai', { wall: dk(K.wood, 0.2), upper: 'none' }, ({ g, x, y, w, h, gf, gy, sign, L, b }) => {
  // one tiny two-storey bar per ~4.5 tiles: lit upstairs window, kawara eave, paper lanterns, narrow door, hanging sign
  const n = Math.max(1, Math.round(w / 150)), uw = w / n;
  for (let u = 0; u < n; u++) {
    const ux = Math.round(x + u * uw), cw = Math.round(uw), v = hash2(b.x + u * 7, b.y), col: NeonColor = (['pink', 'amber', 'cyan'] as const)[Math.floor(v * 3)];
    if (u) { px(g, ux - 1, y, 2, h, K.ink); px(g, ux + 1, y, 2, h, alpha(K.night, 0.4)); } // party wall between houses
    // upstairs
    if (gy - y >= 36) {
      const wx = ux + 14, wy = y + 14;
      windowPane(g, wx, wy, 26, 20, 0.2 + v * 0.2, L);
      px(g, wx - 5, wy - 1, 4, 22, K.woodLo); px(g, wx + 27, wy - 1, 4, 22, K.woodLo); // shutters
      px(g, wx - 5, wy + 22, 36, 3, K.ink); for (let i = 0; i < 36; i += 6) px(g, wx - 5 + i, wy + 24, 2, 6, K.ink); // little balcony rail
      const txt = u === 0 && sign && [...sign.text].length <= 4 ? sign.text : GAI_TEXT[Math.floor(hash2(b.x, u + b.y) * 4)];
      if ([...txt].length <= 2) owBlade(g, ux + cw - 34, y + 10, txt, col, L, 2);
      else owBand(g, ux + cw - 36, y + 12, txt, col, L, 1);
    }
    // kawara eave
    px(g, ux, gy - 12, cw, 8, K.kawara); for (let i = 0; i < cw; i += 6) px(g, ux + i, gy - 12, 1, 8, K.kawaraLo); px(g, ux, gy - 12, cw, 1, K.kawaraHi); px(g, ux, gy - 4, cw, 3, alpha(K.night, 0.5));
    // ground floor: narrow door with noren, barred window, lantern string
    const dx = ux + 12, top = gy + 8;
    door(g, dx, top, 28, gf - 10, K.amber, L); noren(g, dx, top, 28);
    px(g, ux + cw - 54, top + 2, 40, gf - 18, K.ink); px(g, ux + cw - 53, top + 3, 38, gf - 20, K.warm); bands(g, ux + cw - 53, top + 3, 38, gf - 20, K.warmHi, K.warm, 3);
    for (let i = 8; i < 38; i += 8) px(g, ux + cw - 53 + i, top + 3, 1, gf - 20, K.ink); L(ux + cw - 53, top + 3, 38, gf - 20, K.amber, 0.35, true);
    for (let i = 4; i + 8 < cw; i += 22) lantern(g, ux + i, gy - 3, (i / 22 + u) % 2 ? K.amber : K.red, L);
    px(g, ux + 2, gy - 4, cw - 4, 1, K.ink);
    if (hash2(u, b.x) < 0.6) plant(g, ux + cw - 16, gy + gf - 20);
    else { px(g, ux + cw - 20, gy + gf - 12, 14, 9, K.ink); px(g, ux + cw - 19, gy + gf - 11, 12, 7, K.amber); px(g, ux + cw - 19, gy + gf - 11, 12, 2, K.warmHi); } // beer crates
  }
});

/** Hanabi Shrine (a landmark the 1x art had): a dark wooden hall with lit shoji between dense trees, and a red torii
 *  standing in front of it at the head of the gravel grounds */
registerFront('okuboWestShrine', { wall: K.woodLo, upper: 'none' }, ({ g, x, y, w, gf, gy, L }) => {
  // trees fill the wall either side of the hall
  const hw = Math.min(w - 120, 200), hx = Math.round(x + (w - hw) / 2);
  for (const [tx0, tw] of [[x, hx - x], [hx + hw, x + w - hx - hw]] as const) {
    px(g, tx0, y, tw, gy + gf - y, K.leafLo);
    for (let j = y + 4; j < gy + gf - 8; j += 14) for (let i = tx0 + 6; i < tx0 + tw - 6; i += 16) {
      const r = 9 + Math.floor(hash2(i, j) * 5);
      disc(g, i + 4, j + 6, r, r, K.leaf); disc(g, i + 1, j + 3, r - 4, r - 4, K.leafHi);
    }
  }
  // hall: sloped kawara roof edge, wooden posts, three lit shoji panels and an offering box
  px(g, hx - 8, gy - 22, hw + 16, 14, K.ink); px(g, hx - 6, gy - 20, hw + 12, 10, K.kawara); px(g, hx - 6, gy - 20, hw + 12, 2, K.kawaraHi);
  px(g, hx, gy - 8, hw, gf + 8, K.wood);
  const sw = Math.floor((hw - 40) / 3);
  for (let i = 0; i < 3; i++) {
    const sx = hx + 10 + i * (sw + 10);
    px(g, sx - 1, gy + 2, sw + 2, gf - 20, K.ink); bands(g, sx, gy + 3, sw, gf - 22, K.warmHi, K.warm, 3);
    for (let k = 8; k < sw; k += 8) px(g, sx + k, gy + 3, 1, gf - 22, K.woodLo);
    L(sx, gy + 3, sw, gf - 22, K.amber, 0.4, true);
  }
  px(g, hx + hw / 2 - 16, gy + gf - 18, 32, 12, K.ink); px(g, hx + hw / 2 - 15, gy + gf - 17, 30, 10, K.woodHi);
  for (let i = 3; i < 30; i += 4) px(g, hx + hw / 2 - 15 + i, gy + gf - 15, 2, 6, K.woodLo);
  // torii: two red pillars and the double lintel, with lanterns hanging from it
  const p0 = hx + 16, p1 = hx + hw - 26, ty = gy - 36;
  for (const pxx of [p0, p1]) { px(g, pxx, ty, 10, gy + gf - ty, K.ink); px(g, pxx + 1, ty, 8, gy + gf - ty, K.red); px(g, pxx + 2, ty, 2, gy + gf - ty, lt(K.red, 0.3)); }
  px(g, p0 - 18, ty - 10, p1 - p0 + 46, 10, K.ink); px(g, p0 - 17, ty - 9, p1 - p0 + 44, 6, K.red); px(g, p0 - 17, ty - 9, p1 - p0 + 44, 2, lt(K.red, 0.35));
  px(g, p0 - 6, ty + 6, p1 - p0 + 22, 6, K.ink); px(g, p0 - 5, ty + 7, p1 - p0 + 20, 4, K.redLo);
  lantern(g, p0 + 24, ty + 14, K.amber, L); lantern(g, p1 - 18, ty + 14, K.amber, L);
});

// ---------------------------------------------------------------- Shin-Okubo Parking's south face (the deck is in hd-place-carpark.ts):
// open lower decks over two street shops
/** one open car-park level seen from the street: dark slot, pillars every bay, a rail, parked cars, strip lights, slab */
function openDeck(g: G, x: number, y: number, w: number, deckH: number, L: LightFn, skip = 3) {
  px(g, x, y, w, deckH, K.ink); px(g, x + 2, y + 4, w - 4, deckH - 8, K.night);
  for (let i = 0; i < 7; i++) {
    const cx = x + 20 + i * Math.floor((w - 40) / 7), c = [K.red, K.cyan, K.metalHi, K.amber, K.pink, K.metal, K.cyan][(i + skip) % 7];
    if (i === skip) continue; // an empty bay
    px(g, cx, y + deckH - 20, 30, 12, dk(c, 0.35)); px(g, cx + 6, y + deckH - 26, 18, 7, dk(c, 0.5)); px(g, cx + 7, y + deckH - 25, 7, 3, alpha(K.glassHi, 0.4));
    px(g, cx + 3, y + deckH - 9, 6, 4, K.ink); px(g, cx + 21, y + deckH - 9, 6, 4, K.ink); px(g, cx + 27, y + deckH - 17, 3, 2, K.red);
  }
  for (let i = 0; i <= w; i += 40) px(g, x + Math.min(i, w - 8), y, 8, deckH, K.concreteHi);
  for (let i = 0; i < w; i += 6) px(g, x + i, y + deckH - 14, 2, 10, K.metalHi); px(g, x, y + deckH - 15, w, 2, K.metalHi); // rail
  for (let i = 0; i < w; i += 40) { px(g, x + i + 14, y + 6, 10, 3, lt(K.warm, 0.4)); L(x + i + 4, y + 4, 30, deckH - 10, K.warm, 0.18); } // deck strip lights
  px(g, x, y + deckH, w, 6, K.concreteLo); px(g, x, y + deckH, w, 2, K.concreteHi);
}

/** the car park's taller block behind the roof deck: two more open levels and the ramp up to them */
registerFront('okuboCarParkUpper', { wall: K.concrete, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  const lh = Math.floor((h - 12) / 2);
  openDeck(g, x, y, w, lh, L, 1);
  openDeck(g, x, y + lh + 6, w, lh, L, 5);
  // ramp mouth up to the next level at the east end, with chevrons and an amber frame
  const rw = 70, rx = x + w - rw - 10, ry = y + lh + 12, rh = h - lh - 12;
  px(g, rx, ry, rw, rh, K.ink); px(g, rx + 3, ry + 3, rw - 6, rh - 3, dk(K.asphalt, 0.2));
  for (let k = 0; k < 3; k++) for (let s = 0; s < 12; s++) { px(g, rx + 14 + k * 14 + s, ry + rh - 10 - s, 3, 3, K.amber); px(g, rx + 14 + k * 14 + s, ry + rh - 34 + s, 3, 3, K.amber); }
  frame(g, rx, ry, rw, rh, K.amber, 2);
  text(g, 'UP 5F', rx + rw / 2 - textW('UP 5F', 1) / 2, ry - 9, K.amber, 1);
  // a tall lit PARKING blade at the west end
  const bx = x + 8, bw = 26, bh = h - 8;
  px(g, bx, y + 4, bw, bh, K.ink); px(g, bx + 1, y + 5, bw - 2, bh - 2, dk(K.cyan, 0.8)); frame(g, bx + 2, y + 6, bw - 4, bh - 4, K.cyan);
  px(g, bx + 4, y + 9, bw - 8, bw - 8, K.cyan); text(g, 'P', bx + 9, y + 11, K.white, 2);
  [...'PARK'].forEach((c, i) => text(g, c, bx + 9, y + bw + 6 + i * 14, lt(K.cyan, 0.6), 2));
  L(bx, y + 4, bw, bh, K.cyan, 0.6, true, true);
});

registerFront('okuboCarPark', { wall: K.concrete, upper: 'none' }, ({ g, x, y, w, h, gf, gy, L }) => {
  const deckH = gy - y - 6;
  openDeck(g, x, y, w, deckH, L);
  // ground floor: two shops either side of the stair door with a lit P
  const mid = x + Math.floor(w / 2), top = gy + 6, sh = gf - 10;
  door(g, mid - 18, top, 36, sh, K.cyan, L); frame(g, mid - 21, top - 3, 42, sh + 3, K.cyan);
  px(g, mid - 12, gy - 4, 24, 14, K.ink); px(g, mid - 11, gy - 3, 22, 12, K.cyan); text(g, 'P', mid - 4, gy - 2, K.white, 2); L(mid - 14, gy - 6, 28, 18, K.cyan, 0.7, true, true);
  display(g, x + 12, top + 4, mid - 36 - (x + 12), sh - 8, K.amber, L);
  display(g, mid + 36, top + 4, x + w - 12 - (mid + 36), sh - 8, K.pink, L);
  const s1 = 'RAMEN 24', s2 = 'CAR CARE';
  text(g, s1, x + 12 + (mid - 36 - x - 12) / 2 - textW(s1, 1) / 2, top - 2, K.amber, 1);
  text(g, s2, mid + 36 + (x + w - 12 - mid - 36) / 2 - textW(s2, 1) / 2, top - 2, K.pink, 1);
});
